import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";

// Set the target value of link
export const target = (value: any) => {
  return value ? "_blank" : "_self";
};

// Normalize a phone number to Turkish international digits (country code 90).
// A leading "0" (e.g. 0216…) becomes 90216…, matching the WhatsApp/tel convention.
export const toInternationalDigits = (value?: string | null): string => {
  const digits = String(value ?? "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.startsWith("90")) return digits;
  if (digits.startsWith("0")) return `9${digits}`;
  if (digits.startsWith("9")) return digits;
  return `90${digits}`;
};

// Build a tel: href with the +90 country code (empty string → no link).
export const telHref = (value?: string | null): string | undefined => {
  const digits = toInternationalDigits(value);
  return digits ? `tel:+${digits}` : undefined;
};

// Breakpoints using VueUse
export const useIsMobile = () => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile = breakpoints.smaller("lg"); // lg = 1024px
  return { isMobile };
};

export const useIsMobile768 = () => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile768 = breakpoints.smaller("md"); // md = 768px
  return { isMobile768 };
};

// Scroll to bottom function
export const scrollToBottom = () => {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: "smooth",
  });
};

// Scroll to top function
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

/**
 * Maps Strapi mediaSize (e.g. 'aspect-ratio-4/3') to Tailwind aspect classes
 */
export const getAspectClass = (size: string | undefined): string => {
  if (!size) return "aspect-video";
  const aspectMap: Record<string, string> = {
    "aspect-ratio-1/1": "aspect-square",
    "aspect-ratio-3/2": "aspect-[3/2]",
    "aspect-ratio-2/3": "aspect-[2/3]",
    "aspect-ratio-3/4": "aspect-[3/4]",
    "aspect-ratio-4/3": "aspect-[4/3]",
    "aspect-ratio-4/5": "aspect-[4/5]",
    "aspect-ratio-5/4": "aspect-[5/4]",
    "aspect-ratio-16/9": "aspect-video",
    "aspect-ratio-21/9": "aspect-[21/9]",
    "1/1": "aspect-square",
    "3/2": "aspect-[3/2]",
    "2/3": "aspect-[2/3]",
    "3/4": "aspect-[3/4]",
    "4/3": "aspect-[4/3]",
    "4/5": "aspect-[4/5]",
    "5/4": "aspect-[5/4]",
    "16/9": "aspect-video",
    "21/9": "aspect-[21/9]",
    "cover": "aspect-video",
    "contain": "aspect-video",
  };
  return aspectMap[size] || "aspect-video";
};

/**
 * Maps brand color keys to Tailwind background classes
 */
export const getBrandBgClass = (color: string | undefined): string => {
  if (!color) return "";
  const brandClasses: Record<string, string> = {
    first: "bg-brand-first",
    second: "bg-brand-second",
    third: "bg-brand-third",
    "dark-first": "bg-brand-dark",
    "dark-second": "bg-brand-dark-second",
    "light-first": "bg-brand-light",
    "light-second": "bg-brand-light-second",
  };
  return brandClasses[color] || "";
};
